import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { AdminFetchPartnerDriver, AdminFetchPartnerDriverSummary } from 'src/constants/actionTypes'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import {
  getPartnerDriver,
  getPartnerDriverError,
  getPartnerDriverLoading,
  getPartnerDriverNotFound,
} from 'src/selectors/partnerDriver'
import { isUuid } from 'src/utils/partnerDriver/isUuid'

interface Options {
  /** The driver page also needs the summary row (OTP + pending Yardstik); the steps page does not. */
  withSummary: boolean
}

/**
 * The route-param → fetch → refetch wiring shared by the driver page and its steps page.
 *
 * The slice is deliberately not cleared on unmount: moving between the two pages of the same driver
 * keeps the loaded driver on screen while it refetches. A different driver can never flash because
 * `driver` is only returned when its id matches the route, and a request for another id drops the
 * stored one in the reducer.
 */
export const usePartnerDriverLoader = ({ withSummary }: Options) => {
  const { partnerDriverId } = useParams<{ partnerDriverId: string }>()
  const location = useLocation()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const storedDriver = useSelector(getPartnerDriver)
  const isDriverLoading = useSelector(getPartnerDriverLoading)
  const driverError = useSelector(getPartnerDriverError)
  const isNotFound = useSelector(getPartnerDriverNotFound)

  const driver = storedDriver && storedDriver.id === partnerDriverId ? storedDriver : null

  // The param goes straight into API paths and a query string, so anything but a UUID never
  // reaches a request and lands on the not-found card.
  const isIdValid = isUuid(partnerDriverId)

  // The search page passes its query string in navigation state, and the driver/steps pages pass
  // it on to each other; a deep link has none and goes back to the empty search.
  const searchBackTo = useMemo(() => {
    const search = (location.state as { search?: string } | null)?.search ?? ''

    return { pathname: ADMIN_ROUTES.UPDATE_PARTNER_DRIVER_URL, search }
  }, [location.state])

  const refreshDriver = useCallback(() => {
    if (!isUuid(partnerDriverId)) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchPartnerDriver.request({ partnerDriverId }))
  }, [asyncDispatchWithErrorToast, partnerDriverId])

  const refreshSummary = useCallback(() => {
    if (!isUuid(partnerDriverId)) {
      return
    }

    // Silent: the Yardstik card shows its own error with Retry, and the rest of the page works.
    asyncDispatchWithErrorToast(AdminFetchPartnerDriverSummary.request({ partnerDriverId }), true)
  }, [asyncDispatchWithErrorToast, partnerDriverId])

  const refreshAll = useCallback(() => {
    refreshDriver()

    if (withSummary) {
      refreshSummary()
    }
  }, [refreshDriver, refreshSummary, withSummary])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    refreshAll()
  }, [isAuthenticated, refreshAll])

  return {
    driver,
    isIdValid,
    isDriverLoading,
    driverError,
    isNotFound,
    searchBackTo,
    navigationState: location.state,
    refreshDriver,
    refreshSummary,
    refreshAll,
  }
}
